import { Component, Element, Prop, h, State } from '@stencil/core';

import { LogoWhite } from "./logo-white"
import { LogoBlue } from "./logo-blue"

/**
 * A header component for Penn Libraries websites that displays the Penn Libraries logo,
 * service branding, and navigation. Supports both light and dark themes.
 * 
 * @component
 * @example
 * <pennlibs-header 
 *   service-name="Find Books" 
 *   service-lede="Search our catalog and collections"
 *   theme="light">
 *   <a href="/search">Search</a>
 *   <a href="/help">Help</a>
 * </pennlibs-header>
 * 
 * @slot end - Content to display at the end of the header (right side)
 * @slot name-end - Content to display at the end of the service name (right side)
 * 
 * @prop --max-width: The maximum width of the header.
 */
@Component({
  tag: 'pennlibs-header',
  styleUrl: 'pennlibs-header.css',
  shadow: true,
  assetsDirs: ['assets']
})
export class Header {
  /**
   * The service name should clearly describe what users want to accomplish. Avoid technical jargon, technology names, and acronyms.
   */
  @Prop() serviceName?: string;

  /**
   * A brief, compelling description that helps users understand what they can do here. Keep it short and focused on user benefits.
   */
  @Prop() serviceLede?: string;

  /**
   * The URL where the service name links to, typically the service homepage or main landing page.
   * @default "/"
   */
  @Prop() serviceHref?: string = "/";

  /**
   * The visual theme of the header. Light theme uses blue logo, dark theme uses white logo.
   * @default 'light'
   */
  @Prop() theme: 'light' | 'dark' = 'light';

  @State() isMenuOpen: boolean = false;
  @State() navigation = [];

  @Element() hostElement: HTMLElement;

  componentWillLoad() {
    if (this.navigation.length === 0) {
      this.setNavigation();
    }
  }

  handleToggleMenu() {
    this.isMenuOpen = !this.isMenuOpen
  }

  setNavigation() {
    this.navigation = Array.from(this.hostElement.children).reduce((acc, element) => {
      if (element.hasAttribute('slot')) {
        return acc;
      }

      switch (element.tagName) {
        case 'A':
        case 'FORM':
          acc = acc.concat(element)
        default:
          break;
      }

      return acc;
    }, []);
  }

  renderMenuIcon() {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path fill="currentColor" d="M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z"/></svg>
    )
  }

  render() {
    return (
      <header class={`viewport-margins website-header ${this.theme === 'dark' && 'website-header--dark'}`}>
        <div class="website-header__inner-container">
          <div class="website-header__logo-name-container">
            <a href="https://www.library.upenn.edu/" class="website-header_logo-link" aria-label="Penn Libraries">
            {this.theme === 'dark' ? <LogoWhite /> : <LogoBlue />}
            </a>
            {this.serviceName && (
              <a href="/" class="website-header__service-link">
                <span class="website-header__service-name">{this.serviceName}</span>
                {this.serviceLede && <span class="website-header__service-lede">{this.serviceLede}</span>}
              </a>
            )}
            <slot name="name-end" />
          </div>

          {this.navigation.length > 0 && (
            <nav class="website-header__navigation">
              <button class="website-header__navigation-button" aria-expanded={`${this.isMenuOpen}`} onClick={() => this.handleToggleMenu()}>
              {this.renderMenuIcon()} Menu
              </button>
              {this.navigation && (
                <ol class={`website-header__navigation-list ${this.isMenuOpen && 'website-header__navigation-list--visible'}`}>
                  {this.navigation.map(element => <li innerHTML={element.outerHTML} />)}
                </ol>
              )}
            </nav>
          )}

          <slot name="end" />
        </div>
      </header>
    );
  }
}